/**
 * WorkflowFlowTab — State machine visualization for workflow entities
 *
 * Renders the workflow's states as nodes and transitions as edges
 * using @xyflow/react. Fetches full state/transition data via getWorkflow.
 *
 * Node colors by GqlStateType:
 *   INITIAL  → green
 *   FINAL    → blue
 *   CHOICE   → orange (diamond-ish)
 *   PARALLEL → purple
 *   NORMAL   → gray
 *
 * DUMB FRONTEND: All state/transition data comes from backend GraphQL.
 */

import { memo, useCallback, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
  MarkerType,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Alert, Badge, Button, Center, Collapse, Divider, Drawer, Group, Loader, Menu, Paper, SegmentedControl, Stack, Text, ThemeIcon, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconCircleCheck, IconPlayerPlay, IconRoute, IconShieldCheck, IconAlertTriangle, IconX } from '@tabler/icons-react';
import { useMutation as useGqlMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useGetWorkflowQuery, useGetInstanceHistoryQuery, GqlStateType, SimulationScenario, type Instance } from '@/generated/graphql';
import { useTenantId } from '@/auth';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import { InstanceOperationsTab } from './InstanceOperationsTab';

// ============================================================================
// SIMULATION — DRR-170 proofing integration
// ============================================================================

const SIMULATE_SCENARIO = gql`
  mutation SimulateScenario($workflowId: String!, $scenario: SimulationScenario!) {
    simulateScenario(workflowId: $workflowId, scenario: $scenario) {
      workflowId
      summary {
        totalStates
        reachableStates
        unreachableStates
        deadEndStates
        finalStates
        longestPathLength
        totalTransitionsExplored
        uncertainTransitions
        selfLoopStates
        policyGaps
        errors { errorType stateName message }
        warnings { warningType stateName message }
      }
    }
  }
`;

interface SimSummary {
  totalStates: number;
  reachableStates: number;
  unreachableStates: string[];
  deadEndStates: string[];
  finalStates: string[];
  longestPathLength: number;
  totalTransitionsExplored: number;
  uncertainTransitions: number;
  selfLoopStates: string[];
  policyGaps: string[];
  errors: Array<{ errorType: string; stateName: string; message: string }>;
  warnings: Array<{ warningType: string; stateName: string; message: string }>;
}

interface SimResult {
  simulateScenario: {
    workflowId: string;
    summary: SimSummary;
  };
}

// ============================================================================
// TYPES
// ============================================================================

interface WorkflowFlowTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
}

// ============================================================================
// LAYOUT — BFS from initialState, left→right columns
// ============================================================================

const NODE_W = 180;
const NODE_H = 52;
const COL_GAP = 280;
const ROW_GAP = 120;

interface StateData {
  name: string;
  stateType: string;
  policyRefs?: string[];
}

type FlowDirection = 'horizontal' | 'vertical';

function computeLayout(
  states: Array<{ name: string; stateType: string }>,
  transitions: Array<{ fromState: string; toState: string }>,
  initialState: string,
  direction: FlowDirection = 'horizontal',
): Record<string, { x: number; y: number }> {
  // BFS to assign column depth
  const depth: Record<string, number> = {};
  const visited = new Set<string>();
  const queue: string[] = [initialState];
  depth[initialState] = 0;

  const adjacency: Record<string, string[]> = {};
  for (const t of transitions) {
    if (!adjacency[t.fromState]) adjacency[t.fromState] = [];
    adjacency[t.fromState]!.push(t.toState);
  }

  while (queue.length > 0) {
    const curr = queue.shift()!;
    if (visited.has(curr)) continue;
    visited.add(curr);
    for (const next of adjacency[curr] ?? []) {
      if (!(next in depth)) {
        depth[next] = (depth[curr] ?? 0) + 1;
      }
      if (!visited.has(next)) queue.push(next);
    }
  }

  // Assign unvisited states (unreachable) a column after all visited
  const maxDepth = Math.max(0, ...Object.values(depth));
  for (const s of states) {
    if (!(s.name in depth)) depth[s.name] = maxDepth + 1;
  }

  // Group by column, sort rows within each column
  const columns: Record<number, string[]> = {};
  for (const [name, col] of Object.entries(depth)) {
    if (!columns[col]) columns[col] = [];
    columns[col]!.push(name);
  }

  const positions: Record<string, { x: number; y: number }> = {};
  for (const [colStr, names] of Object.entries(columns)) {
    const col = Number(colStr);
    names.forEach((name, row) => {
      const totalRows = names.length;
      const offset = -((totalRows - 1) * ROW_GAP) / 2;
      if (direction === 'vertical') {
        // Top→bottom: columns become rows, rows become columns
        positions[name] = {
          x: offset + row * ROW_GAP,
          y: col * COL_GAP,
        };
      } else {
        // Left→right (default)
        positions[name] = {
          x: col * COL_GAP,
          y: offset + row * ROW_GAP,
        };
      }
    });
  }

  return positions;
}

// ============================================================================
// NODE STYLE by stateType
// ============================================================================

function nodeStyle(stateType: string): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    border: '2px solid',
    minWidth: NODE_W,
    minHeight: NODE_H,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  };
  switch (stateType) {
    case GqlStateType.INITIAL:
      return { ...base, background: '#d3f9d8', borderColor: '#2f9e44', color: '#1c4a24' };
    case GqlStateType.FINAL:
      return { ...base, background: '#dbe4ff', borderColor: '#4263eb', color: '#1e2e6e' };
    case GqlStateType.CHOICE:
      return { ...base, background: '#fff3bf', borderColor: '#f08c00', color: '#5c3d00', borderRadius: 0 };
    case GqlStateType.PARALLEL:
      return { ...base, background: '#f3d9fa', borderColor: '#ae3ec9', color: '#4a1463' };
    default:
      return { ...base, background: '#f1f3f5', borderColor: '#868e96', color: '#212529' };
  }
}

// ============================================================================
// CUSTOM NODE — renders policy shield badge (DRR-129)
// ============================================================================

function formatLabel(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const PolicyStateNode = memo(({ data }: NodeProps) => {
  const policyRefs = (data.policyRefs as string[]) ?? [];
  const hasPolicies = policyRefs.length > 0;
  const isVisited = data.isVisited as boolean;
  const rawName = data.label as string;
  const description = data.description as string | undefined;

  return (
    <Tooltip
      label={description || rawName}
      position="top"
      withArrow
      disabled={!description}
      multiline
      w={250}
    >
    <div style={data.nodeStyle as React.CSSProperties}>
      <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
      <span>{formatLabel(rawName)}</span>
      {isVisited && (
        <span
          style={{
            position: 'absolute',
            top: -8,
            left: -8,
            background: '#94d82d',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        >
          <IconCircleCheck size={12} color="#fff" />
        </span>
      )}
      {hasPolicies && (
        <Tooltip
          label={`Gated by: ${policyRefs.join(', ')}`}
          position="top"
          withArrow
          multiline
          w={240}
        >
          <span
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              background: '#2f9e44',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          >
            <IconShieldCheck size={12} color="#fff" />
          </span>
        </Tooltip>
      )}
      <Handle type="source" position={Position.Right} style={{ visibility: 'hidden' }} />
    </div>
    </Tooltip>
  );
});
PolicyStateNode.displayName = 'PolicyStateNode';

const nodeTypes = { policyState: PolicyStateNode };

// ============================================================================
// COMPONENT
// ============================================================================

export function WorkflowFlowTab({ entity, primitiveType }: Readonly<WorkflowFlowTabProps>) {
  const tenantId = useTenantId() ?? '';
  const [direction, setDirection] = useState<FlowDirection>('vertical');
  const [showGraph, setShowGraph] = useState(true);
  const [showOps, setShowOps] = useState(true);
  const [simResult, setSimResult] = useState<SimSummary | null>(null);
  const [showSim, setShowSim] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<SimulationScenario>(SimulationScenario.ALL_PATHS);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const canvasHeight = 'calc(100vh - 350px)';
  // For workflow primitives, entity.id IS the workflow definition ID.
  // For instance primitives, workflowDefinitionId points to the definition.
  const instanceData = entity as Record<string, unknown> | null;
  const workflowId = primitiveType === 'instance'
    ? instanceData?.workflowDefinitionId as string | undefined
    : entity?.id as string | undefined;
  const instanceId = primitiveType === 'instance' ? instanceData?.id as string : '';

  const { data, loading, error } = useGetWorkflowQuery({
    variables: { workflowId: workflowId ?? '' },
    skip: !workflowId,
    fetchPolicy: 'cache-first',
  });

  const wf = data?.getWorkflow;

  // Instance history — derive visited states for live map visualization
  const { data: historyData } = useGetInstanceHistoryQuery({
    variables: { tenantId, workflowDefinitionId: workflowId ?? '', instanceId },
    skip: primitiveType !== 'instance' || !workflowId || !instanceId,
  });

  const visitedStates = useMemo<Set<string>>(() => {
    const history = historyData?.instanceApiGetInstanceHistory ?? [];
    const states = new Set<string>();
    for (const entry of history) {
      if (entry.currentState) states.add(entry.currentState);
    }
    return states;
  }, [historyData]);

  const currentState = primitiveType === 'instance'
    ? instanceData?.currentState as string | undefined
    : undefined;

  // DRR-170: Simulation proofing
  const [runSimulation, { loading: simLoading }] = useGqlMutation<SimResult>(SIMULATE_SCENARIO);
  const handleSimulate = useCallback(async (scenario: SimulationScenario) => {
    if (!workflowId) return;
    setSelectedScenario(scenario);
    try {
      const result = await runSimulation({
        variables: { workflowId, scenario },
      });
      if (result.data?.simulateScenario?.summary) {
        setSimResult(result.data.simulateScenario.summary);
        setShowSim(true);
      }
    } catch (e) {
      console.error('Simulation failed:', e);
    }
  }, [workflowId, runSimulation]);

  const { nodes, edges } = useMemo<{ nodes: Node[]; edges: Edge[] }>(() => {
    if (!wf?.states?.length) return { nodes: [], edges: [] };

    const positions = computeLayout(wf.states, wf.transitions ?? [], wf.initialState, direction);

    const nodes: Node[] = wf.states.map((s) => {
      const refs = (s as StateData).policyRefs ?? [];
      const style = nodeStyle(s.stateType);
      const isCurrent = currentState === s.name;
      const isVisited = !isCurrent && visitedStates.has(s.name);
      return {
        id: s.name,
        type: 'policyState',
        position: positions[s.name] ?? { x: 0, y: 0 },
        data: {
          label: s.name,
          description: (s as Record<string, unknown>).description as string | undefined,
          policyRefs: refs,
          isVisited,
          nodeStyle: {
            ...style,
            cursor: 'pointer',
            position: 'relative' as const,
            ...(isCurrent ? {
              borderColor: '#f76707',
              borderWidth: 3,
              boxShadow: '0 0 12px rgba(247, 103, 7, 0.5)',
              background: '#fff4e6',
            } : isVisited ? {
              borderColor: '#94d82d',
              borderWidth: 2,
              background: '#f4fce3',
            } : {}),
          },
        },
      };
    });

    const edgeCounts: Record<string, number> = {};
    const edges: Edge[] = (wf.transitions ?? []).map((t, idx) => {
      const key = `${t.fromState}→${t.toState}`;
      edgeCounts[key] = (edgeCounts[key] ?? 0) + 1;

      // Context-aware edge styling for instance views
      const isFromCurrent = currentState === t.fromState;
      const bothVisited = visitedStates.has(t.fromState) && (visitedStates.has(t.toState) || currentState === t.toState);

      let edgeStyle: React.CSSProperties = { stroke: '#868e96' };
      let animated = false;

      if (primitiveType === 'instance' && isFromCurrent) {
        // Available transitions from current state — orange, animated
        edgeStyle = { stroke: '#f76707', strokeWidth: 2.5 };
        animated = true;
      } else if (primitiveType === 'instance' && bothVisited) {
        // Path already taken — green dashed
        edgeStyle = { stroke: '#94d82d', strokeWidth: 2, strokeDasharray: '6,4' };
      } else if (primitiveType !== 'instance') {
        // Workflow definition view — animate from initial
        animated = t.fromState === wf.initialState;
      }

      return {
        id: `e${idx}`,
        source: t.fromState,
        target: t.toState,
        label: formatLabel(t.event),
        animated,
        markerEnd: { type: MarkerType.ArrowClosed },
        style: edgeStyle,
        labelStyle: { fontSize: 10, fill: '#495057' },
        labelBgStyle: { fill: '#fff', fillOpacity: 0.85 },
      };
    });

    return { nodes, edges };
  }, [wf, direction, currentState, visitedStates, primitiveType]);

  // State detail for the drawer
  const selectedStateData = useMemo(() => {
    if (!selectedState || !wf?.states) return null;
    return wf.states.find(s => s.name === selectedState) ?? null;
  }, [selectedState, wf]);

  const selectedStateHistory = useMemo(() => {
    if (!selectedState) return null;
    const history: Instance[] = historyData?.instanceApiGetInstanceHistory ?? [];
    return history.find((h: Instance) => h.currentState === selectedState) ?? null;
  }, [selectedState, historyData]);

  if (!workflowId) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select a workflow to view its state machine.</Text>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">Loading workflow states...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return <Alert color="red" title="Flow Unavailable">{error.message}</Alert>;
  }

  if (!wf?.states?.length) {
    return (
      <Alert color="yellow" title="State Machine Not Available">
        {wf
          ? `This workflow uses ${wf.executionStrategy ?? 'unknown'} execution strategy. DAG and actor-based workflows use task graphs instead of state machines.`
          : `Workflow definition${workflowId ? ` (${workflowId})` : ''} not loaded. The workflow config may need to be seeded.`
        }
      </Alert>
    );
  }

  return (
    <Stack gap="xs">
      {/* ── Graph Section ── */}
      <div>
        <Group gap="xs">
          <Group gap={4} style={{ cursor: 'pointer' }} onClick={() => setShowGraph(g => !g)}>
            {showGraph ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            <Text size="sm" fw={600}>Workflow Map</Text>
          </Group>
          <SegmentedControl
            size="xs"
            value={direction}
            onChange={(val) => { setDirection(val as FlowDirection); if (!showGraph) setShowGraph(true); }}
            data={[
              { label: 'Vertical', value: 'vertical' },
              { label: 'Horizontal', value: 'horizontal' },
            ]}
            onClick={(e) => e.stopPropagation()}
          />
          {/* DRR-170/235: Simulate dropdown — all 4 scenarios */}
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <Button
                size="xs"
                variant="light"
                color="violet"
                leftSection={<IconPlayerPlay size={14} />}
                rightSection={<IconChevronDown size={12} />}
                loading={simLoading}
                onClick={(e) => e.stopPropagation()}
              >
                Simulate
              </Button>
            </Menu.Target>
            <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
              <Menu.Label>Simulation Scenarios (DRR-170)</Menu.Label>
              <Menu.Item
                leftSection={<IconRoute size={14} />}
                onClick={() => handleSimulate(SimulationScenario.ALL_PATHS)}
              >
                All Paths (BFS)
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPlayerPlay size={14} />}
                onClick={() => handleSimulate(SimulationScenario.HAPPY_PATH)}
              >
                Happy Path
              </Menu.Item>
              <Menu.Item
                leftSection={<IconAlertTriangle size={14} />}
                onClick={() => handleSimulate(SimulationScenario.TIMEOUT_PATH)}
              >
                Timeout Path
              </Menu.Item>
              <Menu.Item
                leftSection={<IconX size={14} />}
                onClick={() => handleSimulate(SimulationScenario.REJECTION_PATH)}
              >
                Rejection Path
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </div>
      <Collapse in={showGraph}>
        <div key={direction} style={{ height: canvasHeight, width: '100%', border: '1px solid #dee2e6', borderRadius: 8 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            attributionPosition="bottom-right"
            proOptions={{ hideAttribution: true }}
            onNodeClick={(_, node) => setSelectedState(node.id)}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#dee2e6" />
            <Controls showInteractive={false} />
            <MiniMap
              nodeStrokeWidth={3}
              nodeColor={(n) => (n.style?.borderColor as string) ?? '#868e96'}
              pannable
              zoomable
            />
          </ReactFlow>
        </div>
      </Collapse>

      {/* ── Simulation Results (DRR-170) ── */}
      {simResult && (
        <>
          <UnstyledButton onClick={() => setShowSim(s => !s)}>
            <Group gap="xs">
              {showSim ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              <Text size="sm" fw={600}>Simulation Results</Text>
              <Badge size="xs" variant="outline" color="violet">{selectedScenario.replace('_', ' ')}</Badge>
              {simResult.errors.length === 0 ? (
                <Badge size="xs" color="green">PASS</Badge>
              ) : (
                <Badge size="xs" color="red">{simResult.errors.length} error(s)</Badge>
              )}
              {simResult.warnings.length > 0 && (
                <Badge size="xs" color="yellow">{simResult.warnings.length} warning(s)</Badge>
              )}
            </Group>
          </UnstyledButton>
          <Collapse in={showSim}>
            <Paper p="sm" withBorder>
              <Stack gap="xs">
                <Group gap="lg">
                  <Text size="xs">States: {simResult.reachableStates}/{simResult.totalStates} reachable</Text>
                  <Text size="xs">Finals: {simResult.finalStates.length}</Text>
                  <Text size="xs">Longest path: {simResult.longestPathLength}</Text>
                  <Text size="xs">Transitions: {simResult.totalTransitionsExplored}</Text>
                  {simResult.uncertainTransitions > 0 && (
                    <Text size="xs" c="yellow">Uncertain: {simResult.uncertainTransitions}</Text>
                  )}
                </Group>
                {simResult.errors.map((e, i) => (
                  <Alert key={i} color="red" title={e.errorType} p="xs">
                    <Text size="xs">{e.stateName}: {e.message}</Text>
                  </Alert>
                ))}
                {simResult.warnings.map((w, i) => (
                  <Alert key={i} color="yellow" title={w.warningType} p="xs">
                    <Text size="xs">{w.message}</Text>
                  </Alert>
                ))}
                {simResult.selfLoopStates.length > 0 && (
                  <Text size="xs" c="dimmed">Self-loops: {simResult.selfLoopStates.join(', ')}</Text>
                )}
                {simResult.policyGaps.length > 0 && (
                  <Alert color="red" title="Policy Gaps (DRR-170-A1 §24)" p="xs">
                    <Text size="xs">States with policy_refs but no evaluate_policy: {simResult.policyGaps.join(', ')}</Text>
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Collapse>
        </>
      )}

      {/* ── Operations Section ── */}
      {primitiveType === 'instance' && entity && (
        <>
          <UnstyledButton onClick={() => setShowOps(o => !o)}>
            <Group gap="xs">
              {showOps ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              <Text size="sm" fw={600}>State &amp; Actions</Text>
            </Group>
          </UnstyledButton>
          <Collapse in={showOps}>
            <InstanceOperationsTab
              entity={entity as Record<string, unknown>}
              tenantId=""
              primitiveType="instance"
            />
          </Collapse>
        </>
      )}

      {/* ── State Detail Drawer ── */}
      <Drawer
        opened={!!selectedState}
        onClose={() => setSelectedState(null)}
        position="right"
        size="sm"
        title={selectedState ? formatLabel(selectedState) : ''}
        overlayProps={{ backgroundOpacity: 0.1 }}
      >
        {selectedStateData && (
          <Stack gap="md">
            {/* Status */}
            <Group gap="xs">
              <Badge
                color={currentState === selectedState ? 'orange' : visitedStates.has(selectedState!) ? 'green' : 'gray'}
                variant="light"
              >
                {currentState === selectedState ? 'Current' : visitedStates.has(selectedState!) ? 'Completed' : 'Pending'}
              </Badge>
              <Badge variant="outline" size="sm">
                {selectedStateData.stateType}
              </Badge>
            </Group>

            {/* Description */}
            {!!(selectedStateData as Record<string, unknown>).description && (
              <Text size="sm" c="dimmed">
                {String((selectedStateData as Record<string, unknown>).description)}
              </Text>
            )}

            {/* Timestamp from history */}
            {selectedStateHistory && (
              <>
                <Divider label="Entered" labelPosition="left" />
                <Text size="xs" c="dimmed">
                  {new Date(((selectedStateHistory as Record<string, unknown>).audit as Record<string, unknown>)?.createdAt as string ?? 0).toLocaleString()}
                </Text>
              </>
            )}

            {/* Policy Refs */}
            {((selectedStateData as StateData).policyRefs ?? []).length > 0 && (
              <>
                <Divider label="Policy Gates" labelPosition="left" />
                <Stack gap={4}>
                  {((selectedStateData as StateData).policyRefs ?? []).map((ref: string) => (
                    <Paper key={ref} p="xs" withBorder style={{ backgroundColor: 'var(--mantine-color-green-0)' }}>
                      <Group gap="xs">
                        <ThemeIcon size="xs" variant="light" color="green"><IconShieldCheck size={10} /></ThemeIcon>
                        <Text size="xs" fw={500}>{ref}</Text>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}

            {/* Entry Actions */}
            {((selectedStateData as Record<string, unknown>).entryActions as string[] | undefined)?.length ? (
              <>
                <Divider label="Entry Actions" labelPosition="left" />
                <Stack gap={4}>
                  {((selectedStateData as Record<string, unknown>).entryActions as string[]).map((action: string) => (
                    <Paper key={action} p="xs" withBorder>
                      <Text size="xs" fw={500}>{formatLabel(action)}</Text>
                    </Paper>
                  ))}
                </Stack>
              </>
            ) : null}

            {/* Outgoing Transitions */}
            {wf?.transitions && (
              <>
                <Divider label="Transitions" labelPosition="left" />
                <Stack gap={4}>
                  {wf.transitions
                    .filter(t => t.fromState === selectedState)
                    .map((t, i) => (
                      <Paper key={i} p="xs" withBorder>
                        <Group justify="space-between">
                          <Text size="xs" fw={500}>{formatLabel(t.event)}</Text>
                          <Badge size="xs" variant="outline">{formatLabel(t.toState)}</Badge>
                        </Group>
                      </Paper>
                    ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
